import { useContext } from 'react';
import useSmartState from '../../../../common/hooks/useSmartState';
import { SmartContext } from '../../../../library/Core/SmartContext';

const formatDateTime = (value?: string) => {
    if (!value) return '-';
    try {
        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(value));
    } catch (error) {
        return value;
    }
};

const getStatusBadgeClass = (status?: string) => {
    switch ((status || '').toUpperCase()) {
        case 'NEW':
            return 'bg-primary-subtle text-primary';
        case 'VIEWED':
            return 'bg-info-subtle text-info';
        case 'RESPONDED':
            return 'bg-success-subtle text-success';
        case 'CLOSED':
            return 'bg-secondary-subtle text-secondary';
        default:
            return 'bg-secondary-subtle text-secondary';
    }
};

const getOutcomeBadgeClass = (outcome?: string) => {
    switch ((outcome || '').toUpperCase()) {
        case 'PENDING':
            return 'bg-warning-subtle text-warning';
        case 'CONVERTED':
            return 'bg-success-subtle text-success';
        case 'NOT_TAKEN_UP':
            return 'bg-secondary-subtle text-secondary';
        case 'DECLINED':
            return 'bg-danger-subtle text-danger';
        default:
            return 'bg-secondary-subtle text-secondary';
    }
};

interface ResultsTableProps {
    onViewInquiry: (inquiry: any) => void;
}

const ResultsTable = ({ onViewInquiry }: ResultsTableProps) => {
    const { state } = useContext(SmartContext);
    const { setStateValue } = useSmartState();

    const inquiryList = state.data?.inquiryList || [];
    const pagination = state.data?.pagination || { total: 0, page: 1, limit: 20 };
    const resultsSection: any = state.formConfig?.sectionRepository?.find((section: any) => section.id === 'results');
    const columns = resultsSection?.controlGroup?.[0]?.props?.columns || [];
    const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));

    const renderCell = (row: any, accessorKey: string) => {
        switch (accessorKey) {
            case 'inquirer':
                return (
                    <div className="d-flex flex-column">
                        <span className="font-16 font-500 text-dark">{row.inquirerName || '-'}</span>
                        <span className="font-13 text-muted">{row.inquirerEmail || '-'}</span>
                    </div>
                );
            case 'subject':
                return <span className="font-14 font-500 text-dark">{row.subject || '-'}</span>;
            case 'received':
                return <span className="font-14">{formatDateTime(row.createdAt)}</span>;
            case 'status':
                return <span className={`badge rounded-pill px-3 py-2 font-13 ${getStatusBadgeClass(row.status)}`}>{row.status || 'UNKNOWN'}</span>;
            case 'respondedOn':
                return <span className="font-14">{formatDateTime(row.respondedAt)}</span>;
            case 'outcome':
                return <span className={`badge rounded-pill px-3 py-2 font-13 ${getOutcomeBadgeClass(row.outcome)}`}>{row.outcome || 'UNKNOWN'}</span>;
            case 'action':
                return (
                    <button type="button" className="btn btn-skyblue btn-sm px-3" onClick={() => onViewInquiry(row)}>
                        View
                    </button>
                );
            default:
                return <span>{row[accessorKey] || '-'}</span>;
        }
    };

    const goToPage = (page: number) => {
        setStateValue('searchCriteria', {
            ...state.data?.searchCriteria,
            page,
        } as any);
    };

    if (inquiryList.length === 0) {
        return (
            <div className="text-center p-5 bg-white border rounded-3">
                <i className="icon-inbox font-48 text-muted d-block mb-3"></i>
                <h5 className="font-16 font-600">No inquiries found</h5>
                <p className="text-muted mb-0">Try adjusting your filters or search terms.</p>
            </div>
        );
    }

    return (
        <div className="results-section">
            <div className="results-header d-flex justify-content-between align-items-center mb-3 gap-3">
                <div className="results-count">
                    <span className="text-muted">
                        Showing {inquiryList.length} of {pagination.total} inquiries
                    </span>
                </div>
            </div>

            <div className="table-responsive attendee_dtl bg-white rounded-3">
                <table className="table mb-0" style={{ minWidth: '980px' }}>
                    <thead>
                        <tr>
                            {columns.map((column: any) => (
                                <th key={column.accessorKey}>{column.header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {inquiryList.map((row: any) => (
                            <tr key={row.id}>
                                {columns.map((column: any) => (
                                    <td key={`${row.id}-${column.accessorKey}`} className="align-middle">
                                        {renderCell(row, column.accessorKey)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <button
                        type="button"
                        className="btn btn-border-gray height-40"
                        onClick={() => goToPage(Math.max(1, pagination.page - 1))}
                        disabled={pagination.page <= 1}>
                        Previous
                    </button>
                    <span className="font-14 text-muted">Page {pagination.page} of {totalPages}</span>
                    <button
                        type="button"
                        className="btn btn-border-gray height-40"
                        onClick={() => goToPage(Math.min(totalPages, pagination.page + 1))}
                        disabled={pagination.page >= totalPages}>
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default ResultsTable;